import { NavigateBefore } from "@mui/icons-material";
import {
  Box,
  Button,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import useApi from "_api/index";
import AlertToast from "components/AlertToast";
import RenderFieldsCars from "components/Car/RenderFieldsCars";
import RenderFieldsCustomers from "components/Car/RenderFieldsCustomers";
import CustomizeDatePicker from "components/DatePicker";
import { GradientButton } from "components/GradientButton";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import * as Yup from "yup";

const ModalConsignmentCar = ({ open, onClose, onSave, isEditMode, id }) => {
  const [customerImage, setCustomerImage] = React.useState([]);
  const [carImage, setCarImage] = React.useState([]);
  const [carImageInitial, setCarImageInitial] = React.useState([]);
  const [dataCar, setDataCar] = React.useState(null);
  const [toast, setToast] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    console.log({ isEditMode, id });
    if (isEditMode && id) {
      const fetchConsignmentCarDetails = async () => {
        try {
          const response = await useApi.consignment_car.getConsignmentCarById(
            id
          );
          if (response.success) {
            console.log("Fetched consignment car details:", response.data);
            const { customer, car, consignment_detail } = response.data;
            const customerInitialValues = {
              prefix: customer.customer_prefix || "",
              fullName: customer.customer_name || "",
              idCardNumber: customer.customer_idcard || "",
              phoneNumber: customer.customer_phonenumber || "",
              address: customer.customer_address || "",
              nationality: customer.customer_nationality || "ไทย",
            };
            setDataCar(car);
            if (customer.customer_image) {
              setCustomerImage([
                {
                  url: customer.customer_image,
                  name: "customer_image.jpg",
                },
              ]);
            } else {
              setCustomerImage([]);
            }
            if (car.car_pictures && car.car_pictures.length > 0) {
              setCarImageInitial(
                car.car_pictures.map((photo) => ({
                  ...photo,
                  name: "car_pictures",
                }))
              );
            }
            const carInitialValues = {
              ...customerInitialValues,
              ...car,
              car_product_type: "consignment_car", // Set default for consignment
              ...consignment_detail,
            };
            // Set initial values for formik
            formik.resetForm({
              values: carInitialValues,
            });
          }
        } catch (error) {
          console.error("Error fetching consignment car details:", error);
        }
      };
      fetchConsignmentCarDetails();
    } else {
      // Reset form to initial values if not in edit mode
      formik.resetForm({
        values: generateInitialValues(),
      });
    }
  }, [isEditMode, id]);

  useEffect(() => {
    if (!open) {
      setCustomerImage([]);
      setCarImage([]);
      setCarImageInitial([]);
      setDataCar(null);
    }
  }, [open]);

  const generateInitialValues = () => {
    return {
      // ข้อมูลลูกค้า
      prefix: "", // "นาย", "นาง", "นางสาว"
      fullName: "",
      idCardNumber: "",
      phoneNumber: "",
      address: "",
      nationality: "ไทย", // "ไทย", "ต่างชาติ"

      // ข้อมูลรถ
      car_name: "",
      car_type: "", // "รถยนต์", "มอเตอร์ไซด์"
      car_no: "",
      license_plate: "",
      license_province: "",
      license_date: "",
      tax_expire_date: "",
      compulsory_expire_date: "",
      characteristic: "",
      car_brand_id: null,
      color: "",
      model: "",
      fuel_type: "",
      engine_brand: "",
      engine_number: "",
      gas_number: null,
      number_of_engine_cylinders: null,
      cc: null,
      horsepower: null,
      weight: null,
      seat: null,
      car_mileage: "",
      car_price: "",
      insurance_company: "" || null,
      insurance_level: "" || null,
      insurance_expire_date: "" || null,
      purchase_type: "cash", // "cash", "installment"
      defect: "" || null,
      car_status_id: 1,
      note: "" || null,
      car_product_type: "consignment_car", // "consignment", "buy", "pawn", "ready_sell"

      // ข้อมูลฝากขาย
      setting_price: null, // = car_price ราคารถ
      percent_for_sale: null, // ค่าบริการ
      total_price: null, // ราคารถสุทธิ
      // consignment_expire: "", // วันที่สิ้นสุดการฝากขาย
    };
  };

  const generateValidationSchema = () => {
    // fields car
    const baseFields = {
      car_name: Yup.string().required("กรุณากรอกรถ"),
      car_type: Yup.string().required("กรุณาเลือกประเภทของรถ"),
      car_no: Yup.string().required("กรุณากรอกเลขตัวถังรถ"),
      license_plate: Yup.string().required("กรุณากรอกหมายเลขทะเบียนรถ"),
      license_province: Yup.string().required("กรุณาเลือกจังหวัดทะเบียนรถ"),
      license_date: Yup.date().required("กรุณาเลือกวันที่ออกทะเบียนรถ"),
      tax_expire_date: Yup.date().required("กรุณาเลือกวันหมดอายุภาษี"),
      compulsory_expire_date: Yup.date().required(
        "กรุณาเลือกวันหมดอายุ พ.ร.บ."
      ),
      characteristic: Yup.string().required("กรุณากรอกรายละเอียดรถ"),
      car_brand_id: Yup.number().required("กรุณาเลือกยี่ห้อรถ"),
      color: Yup.string().required("กรุณาเลือกสีรถ"),
      model: Yup.string().required("กรุณากรอกรุ่นรถ"),
      fuel_type: Yup.string().required("กรุณาเลือกประเภทเชื้อเพลิง"),
      engine_brand: Yup.string().required("กรุณากรอกยี่ห้อเครื่องยนต์"),
      engine_number: Yup.string().required("กรุณากรอกหมายเลขเครื่องยนต์"),
      number_of_engine_cylinders: Yup.number().required(
        "กรุณากรอกจำนวนกระบอกสูบ"
      ),
      cc: Yup.number().required("กรุณากรอกความจุกระบอกสูบ (CC)"),
      horsepower: Yup.number().required("กรุณากรอกแรงม้า"),
      weight: Yup.number().required("กรุณากรอกน้ำหนักรถ"),
      seat: Yup.number().required("กรุณากรอกจำนวนที่นั่ง"),
      car_mileage: Yup.number().required("กรุณากรอกระยะทางที่ใช้ไป (กิโลเมตร)"),
      car_price: Yup.number()
        .required("กรุณากรอกราคารถ")
        .min(1, "กรุณากรอกราคารถ"),
      insurance_level: Yup.string().when("insurance_company", {
        is: (val) => val && val != "",
        then: (schema) => schema.required("กรุณาเลือกชั้นประกันภัย"),
        otherwise: (schema) => schema.notRequired(),
      }),
      insurance_expire_date: Yup.date().when("insurance_company", {
        is: (val) => val && val != "",
        then: (schema) => schema.required("กรุณาเลือกวันหมดอายุประกันภัย"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // purchase_type: Yup.string().required("กรุณาเลือกประเภทการชำระเงิน"),

      setting_price: Yup.number().required("กรุณากรอกราคารถ"),
      percent_for_sale: Yup.number().required("กรุณากรอกค่าบริการ"),
      total_price: Yup.number().required("กรุณากรอกราคารถสุทธิ"),
    };

    // fields customer (validate เฉพาะตอน create)
    const customerFields = {
      prefix: Yup.string().required("กรุณาเลือกคำนำหน้า"),
      fullName: Yup.string().required("กรุณากรอกชื่อ-นามสกุล"),
      idCardNumber: Yup.string().when("nationality", {
        is: (val) => val === "ไทย",
        then: (schema) =>
          schema
            .required("กรุณากรอกเลขบัตรประชาชน")
            .matches(/^\d+$/, "เลขบัตรประชาชนต้องเป็นตัวเลขเท่านั้น")
            .length(13, "เลขบัตรประชาชนต้องมี 13 หลัก"),
        otherwise: (schema) =>
          schema
            .required("กรุณากรอกเลขหนังสือเดินทาง (Passport No.)")
            .matches(
              /^[A-Za-z0-9]+$/,
              "เลขหนังสือเดินทางต้องเป็นตัวอักษรหรือตัวเลขเท่านั้น"
            )
            .min(6, "เลขหนังสือเดินทางต้องมีอย่างน้อย 6 หลัก")
            .max(20, "เลขหนังสือเดินทางกรอกได้มากสุด 20 หลัก"),
      }),
      phoneNumber: Yup.string()
        .required("กรุณากรอกเบอร์โทรศัพท์")
        .min(9, "เบอร์โทรศัพท์ต้องมีอย่างน้อย 9 หลัก")
        .max(10, "เบอร์โทรศัพท์ต้องไม่เกิน 10 หลัก")
        .matches(/^\d+$/, "เบอร์โทรศัพท์ต้องเป็นตัวเลขเท่านั้น"),
      address: Yup.string().required("กรุณากรอกที่อยู่"),
      nationality: Yup.string().required("กรุณาเลือกสัญชาติ"),
    };

    const schemaShape = isEditMode
      ? baseFields
      : { ...customerFields, ...baseFields };
    // console.log({ schemaShape });

    return Yup.object(schemaShape);
  };

  const formik = useFormik({
    initialValues: generateInitialValues(),
    validationSchema: generateValidationSchema(),
    onSubmit: (values) => {
      // Handle form submission
      console.log("Form values:", values);
      if (isEditMode) {
        handleUpdateConsignmentCar(values);
      } else {
        handleCreateConsignmentCar(values);
      }
    },
  });

  useEffect(() => {
    if (formik.values.car_price && formik.values.percent_for_sale) {
      formik.setFieldValue("setting_price", formik.values.car_price);
      const carPrice = Number(formik.values.car_price || 0);
      const percentForSale = Number(formik.values.percent_for_sale || 0);
      const totalPrice = carPrice - carPrice * (percentForSale / 100);
      formik.setFieldValue("total_price", totalPrice);
    } else {
      formik.setFieldValue("total_price", "");
    }
  }, [formik.values.car_price, formik.values.percent_for_sale]);

  const handleCreateConsignmentCar = async (values) => {
    try {
      const data = {
        customer_data: {
          prefix: values.prefix,
          fullName: values.fullName,
          idCardNumber: values.idCardNumber,
          phoneNumber: values.phoneNumber,
          address: values.address,
          nationality: values.nationality || "ไทย",
          customer_type: "car",
        },
        car_data: {
          ...values,
        },
        consignment_data: {
          setting_price: values.car_price, // ราคารถ
          percent_for_sale: values.percent_for_sale, // ค่าบริการ
          total_price: values.total_price, // ราคารถสุทธิ
        },
        car_photos: carImage,
        image: customerImage || [], // Assuming `image` is optional
      };

      // Call the API to create the consignment car
      const response = await useApi.consignment_car.createOrderConsignmentCar(
        data
      );

      if (response?.success || response?.isStatus) {
        console.log("Consignment car created successfully:", response.data);
        onSave(); // Call onSave with the created data
        onClose(); // Close the dialog
      } else {
        console.error("Failed to create consignment car:", response.message);
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Error creating consignment car:", error);
      if (error.message.includes("car_price")) {
        formik.setFieldError("car_price", error.message);
        formik.setTouched({ car_price: true });
        setToast({
          open: true,
          message: "กรุณาตรวจสอบข้อมูลราคารถ เนื่องจากมีราคาสูงผิดปกติ",
          severity: "error",
        });
      } else {
        setToast({
          open: true,
          message: "เกิดข้อผิดพลาดในการสร้างรายการฝากขาย",
          severity: "error",
        });
      }
    }
  };

  const handleUpdateConsignmentCar = async (values) => {
    try {
      console.log({ update: values });
      console.log({ carImage });
      const data = {
        car_data: {
          ...values,
        },
        consignment_data: {
          setting_price: values.car_price, // ราคารถ
          percent_for_sale: values.percent_for_sale, // ค่าบริการ
          total_price: values.total_price, // ราคารถสุทธิ
        },
        car_photos: carImage.filter((image) => image.id === undefined) || [],
      };
      // Call the API to update the consignment car
      const response = await useApi.consignment_car.updateConsignmentCar(
        id,
        data
      );
      if (response.isStatus) {
        console.log("Consignment car updated successfully:", response.data);
        await handleDeleteCarImages(); // Delete images that are not in the new carImage
        onSave(); // Call onSave with the updated data
        onClose(); // Close the dialog
      } else {
        console.error("Failed to update consignment car:", response.message);
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Error updating consignment car:", error);
      if (error.message.includes("car_price")) {
        formik.setFieldError("car_price", error.message);
        formik.setTouched({ car_price: true });
        setToast({
          open: true,
          message: "กรุณาตรวจสอบข้อมูลราคารถ เนื่องจากมีราคาสูงผิดปกติ",
          severity: "error",
        });
      } else {
        setToast({
          open: true,
          message: "เกิดข้อผิดพลาดในการอัปเดตรายการฝากขาย",
          severity: "error",
        });
      }
    }
  };

  const handleDeleteCarImages = async () => {
    let delImg = [];
    if (
      carImageInitial &&
      carImageInitial.length > 0 &&
      carImage &&
      carImage.length > 0
    ) {
      // Find images that are in carImageInitial but not in carImage
      delImg = carImageInitial.filter(
        (img) => !carImage.some((newImg) => newImg.id === img.id)
      );
    }
    if (delImg.length > 0) {
      try {
        const ids = delImg.map((img) => img.id);
        const response = await useApi.consignment_car.deleteCarImages(ids);
        if (response.success) {
          console.log("Deleted car images successfully:", response.data);
        } else {
          console.error("Failed to delete car images:", response.message);
        }
      } catch (error) {
        console.error("Error deleting car images:", error);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableBackdropClick
      fullWidth
      maxWidth="md"
      sx={{
        "& .MuiDialog-paper": {
          padding: 2,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h4">
          {isEditMode ? "แก้ไขรายละเอียดรถ" : "รายการฝากขาย"}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.grey[300],
            borderRadius: 4,
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <RenderFieldsCustomers
              formik={formik}
              setCustomerImage={setCustomerImage}
              images={customerImage}
              disabled={isEditMode}
            />
            <RenderFieldsCars
              sx={{ mt: 2 }}
              formik={formik}
              setCarImage={setCarImage}
              type="consignment"
              images={carImageInitial || []}
              isEdit={isEditMode}
              data={dataCar}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>
                เงื่อนไขการฝากขาย
              </Typography>
              <Box>
                <Grid container spacing={2} sx={{ pl: 2 }}>
                  <Grid item xs={12} sm={12} md={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body1">
                          ราคารถ
                          <span style={{ color: colors.red[500] }}>*</span>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="car_price"
                          type="number"
                          placeholder="ระบุราคารถ"
                          value={formik.values.car_price}
                          onChange={formik.handleChange}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "white",
                              height: "40px",
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          error={
                            formik.touched.car_price &&
                            Boolean(formik.errors.car_price)
                          }
                          helperText={
                            formik.touched.car_price && formik.errors.car_price
                          }
                          inputProps={{
                            min: 0,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Typography variant="body1">บาท</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body1">
                          ค่าบริการ
                          <span style={{ color: colors.red[500] }}>*</span>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="percent_for_sale"
                          placeholder="ระบุค่าบริการ"
                          value={formik.values.percent_for_sale}
                          type="number"
                          onChange={(e) => {
                            console.log({ val: e.target.value });
                            if (e.target.value > 100) {
                              formik.setFieldValue("percent_for_sale", 100);
                            } else if (e.target.value < 0) {
                              formik.setFieldValue("percent_for_sale", 0);
                            } else if (e.target.value === "") {
                              formik.setFieldValue("percent_for_sale", "");
                            } else {
                              formik.handleChange(e);
                            }
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "white",
                              height: "40px",
                            },
                          }}
                          onKeyPress={(event) => {
                            // Allow only numeric input (0-9) และจุดทศนิยม 2 ตำแหน่ง
                            const charCode = event.which
                              ? event.which
                              : event.keyCode;
                            if (
                              charCode !== 46 && // Allow dot
                              (charCode < 48 || charCode > 57)
                            ) {
                              event.preventDefault();
                            } else {
                              // ตรวจสอบทศนิยมไม่เกิน 2 ตำแหน่ง
                              const inputValue = event.target.value;
                              const regex = /^\d*\.?\d{0,2}$/;
                              if (!regex.test(inputValue + event.key)) {
                                // ตัดให้เหลือแค่ 2 ตำแหน่งทศนิยม
                                event.preventDefault();
                              }
                            }
                          }}
                          inputProps={{
                            min: 0,
                            max: 100,
                          }}
                          InputLabelProps={{ shrink: true }}
                          error={
                            formik.touched.percent_for_sale &&
                            Boolean(formik.errors.percent_for_sale)
                          }
                          helperText={
                            formik.touched.percent_for_sale &&
                            formik.errors.percent_for_sale
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Typography variant="body1">%</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body1">ราคารถสุทธิ</Typography>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="total_price"
                          placeholder="ราคารถสุทธิ"
                          value={formik.values.total_price}
                          onChange={formik.handleChange}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "white",
                              height: "40px",
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            readOnly: true,
                            color: colors.green[500],
                          }}
                          error={
                            formik.touched.total_price &&
                            Boolean(formik.errors.total_price)
                          }
                          helperText={
                            formik.touched.total_price &&
                            formik.errors.total_price
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Typography variant="body1">บาท</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          borderTop: `1px solid ${colors.grey[300]}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: 2,
            borderColor: colors.grey[400],
            color: colors.grey[400],
            "&:hover": {
              borderColor: colors.grey[400],
              bgcolor: "transparent",
            },
          }}
        >
          <NavigateBefore
            sx={{
              width: 30,
              height: 30,
            }}
          />
        </Button>
        <GradientButton
          type="submit"
          onClick={() => formik.handleSubmit()}
          sx={{
            width: "auto",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          // disabled={isLoading}
          // loading={isLoading}
        >
          บันทึก
        </GradientButton>
      </DialogActions>

      <AlertToast
        open={toast.open}
        type={toast.severity}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Dialog>
  );
};

export default ModalConsignmentCar;
